import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api, { backupAPI, restoreAPI } from '../services/api';
import { useTheme } from '../context/ThemeContext';

const OUTCOMES = ['POSITIVO', 'NEGATIVO', 'PARZIALE'];
const CADENCES = ['MENSILE', 'TRIMESTRALE', 'SEMESTRALE', 'ANNUALE', 'OCCASIONALE'];
const cadenceLabel = (c) => (c ? c.charAt(0) + c.slice(1).toLowerCase() : '—');
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const MAX_FILES = 12;

const OUTCOME_COLORS = {
  POSITIVO: { bg: '#dcfce7', color: '#16a34a', border: '#86efac' },
  NEGATIVO: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
  PARZIALE: { bg: '#fefce8', color: '#ca8a04', border: '#fde047' },
};

function OutcomeBadge({ outcome }) {
  const c = OUTCOME_COLORS[outcome] || { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12,
      fontWeight: 700, background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      letterSpacing: '0.03em',
    }}>{outcome}</span>
  );
}

function KPICard({ label, value, color, sub }) {
  return (
    <div style={s.kpi}>
      <div style={s.kpiLabel}>{label}</div>
      <div style={{ ...s.kpiValue, color }}>{value}</div>
      {sub && <div style={s.kpiSub}>{sub}</div>}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
}

function todayISO() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Estrae un messaggio leggibile sia dagli errori semplici ({detail: "..."})
// sia da quelli di validazione pydantic ({detail: [{loc, msg}, ...]}).
function errorMessage(err, fallback = 'Operazione non riuscita') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) {
    return d.map(e => `${(e.loc || []).slice(1).join('.') || 'campo'}: ${e.msg}`).join('; ');
  }
  return err?.message || fallback;
}

// Gli screenshot sono protetti da autenticazione: un <img src> non può
// inviare l'header Authorization, quindi si scaricano come blob via axios e
// si mostrano tramite un object URL (revocato allo smontaggio).
function useEvidenceUrl(evidenceId) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    setUrl(null); setFailed(false);
    api.get(`/api/restore-tests/evidence/${evidenceId}`, { responseType: 'blob' })
      .then(r => {
        if (!alive) return;
        objectUrl = window.URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [evidenceId]);
  return { url, failed };
}

function EvidenceThumb({ ev, onOpen, onDelete }) {
  const { url, failed } = useEvidenceUrl(ev.id);
  return (
    <div style={s.thumbBox}>
      {url
        ? <img src={url} alt={ev.filename} style={s.thumbImg} onClick={() => onOpen(url, ev.filename)} />
        : <div style={s.thumbPlaceholder}>{failed ? 'Errore' : '…'}</div>}
      <div style={s.thumbName} title={ev.filename}>{ev.filename}</div>
      {onDelete && (
        <button type="button" style={s.thumbDelete} onClick={() => onDelete(ev)} title="Elimina screenshot">✕</button>
      )}
    </div>
  );
}

function Lightbox({ src, name, onClose }) {
  useEffect(() => {
    const h = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  if (!src) return null;
  return (
    <div style={s.lightbox} onClick={onClose}>
      <img src={src} alt={name} style={s.lightboxImg} onClick={e => e.stopPropagation()} />
      <div style={s.lightboxCaption}>{name} — clic fuori o Esc per chiudere</div>
    </div>
  );
}

function PendingThumb({ item, onRemove }) {
  return (
    <div style={s.thumbBox}>
      <img src={item.url} alt={item.file.name} style={s.thumbImg} />
      <div style={s.thumbName} title={item.file.name}>{item.file.name}</div>
      <button type="button" style={s.thumbDelete} onClick={onRemove} title="Rimuovi">✕</button>
    </div>
  );
}

function RestoreFormModal({ test, customers, infraSuggestions, defaultCustomer, onClose, onSaved }) {
  const editing = !!test;
  const { theme } = useTheme();
  const [form, setForm] = useState({
    customer_code: test?.customer_code || defaultCustomer || '',
    infrastructure: test?.infrastructure || '',
    vm_id: test?.vm_id || '',
    vm_name: test?.vm_name || '',
    tested_at: test?.tested_at ? String(test.tested_at).slice(0, 10) : todayISO(),
    outcome: test?.outcome || 'POSITIVO',
    restore_removed: test ? !!test.restore_removed : false,
    notes: test?.notes || '',
    cadence: test?.cadence || '',
  });
  const [existing, setExisting] = useState([]);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const fileInput = useRef(null);
  const pendingRef = useRef([]);
  pendingRef.current = pending;

  useEffect(() => {
    if (!editing) return;
    restoreAPI.get(test.id).then(r => setExisting(r.data.evidence || [])).catch(() => {});
  }, [editing, test]);

  // Libera gli object URL delle anteprime allo smontaggio del modale.
  useEffect(() => () => pendingRef.current.forEach(p => window.URL.revokeObjectURL(p.url)), []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError('');
    setPending(prev => {
      const out = [...prev];
      for (const file of incoming) {
        if (!file.type.startsWith('image/')) { setError(`'${file.name}' non è un'immagine`); continue; }
        if (file.size > MAX_FILE_BYTES) { setError(`'${file.name}' supera 8 MB`); continue; }
        if (existing.length + out.length >= MAX_FILES) { setError(`Massimo ${MAX_FILES} screenshot per restore`); break; }
        out.push({ file, url: window.URL.createObjectURL(file) });
      }
      return out;
    });
  }, [existing.length]);

  // Incolla (Ctrl+V) uno screenshot dagli appunti direttamente nel modale.
  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length) { e.preventDefault(); addFiles(files); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer?.files);
  };

  const removePending = (idx) => {
    setPending(prev => {
      window.URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const deleteExisting = async (ev) => {
    if (!window.confirm(`Eliminare lo screenshot "${ev.filename}"?`)) return;
    try {
      await restoreAPI.deleteEvidence(ev.id);
      setExisting(list => list.filter(e => e.id !== ev.id));
    } catch (err) { setError(errorMessage(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.customer_code) return setError('Seleziona il cliente');
    if (!form.vm_name.trim()) return setError('Indica il nome della VM');
    setSaving(true);
    const payload = {
      ...form,
      infrastructure: form.infrastructure.trim() || null,
      vm_id: form.vm_id.trim() || null,
      vm_name: form.vm_name.trim(),
      notes: form.notes.trim() || null,
      cadence: form.cadence || null,
    };
    let saved;
    try {
      const res = editing ? await restoreAPI.update(test.id, payload) : await restoreAPI.create(payload);
      saved = res.data;
    } catch (err) {
      setSaving(false);
      return setError(errorMessage(err));
    }
    if (pending.length) {
      try {
        await restoreAPI.uploadEvidence(saved.id, pending.map(p => p.file));
      } catch (err) {
        setSaving(false);
        // Il test è già salvato: si avvisa e si aggiorna l'elenco, senza
        // far credere all'utente che non sia stato registrato nulla.
        onSaved();
        return setError(`Restore salvato, ma upload screenshot non riuscito: ${errorMessage(err)}. Riaprilo con "Modifica" per riprovare.`);
      }
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  const infraListId = 'restore-infra-list';

  return (
    <div style={s.overlay}>
      <form style={s.modal} onSubmit={submit} onPaste={handlePaste}>
        <div style={s.modalHeader}>
          <h3 style={{ margin: 0, fontSize: 17 }}>{editing ? 'Modifica restore test' : 'Nuovo restore test'}</h3>
          <button type="button" style={s.iconBtn} onClick={onClose} aria-label="Chiudi">✕</button>
        </div>

        <div style={s.grid2}>
          <label style={s.field}>
            <span style={s.label}>Cliente *</span>
            <select style={s.input} value={form.customer_code} onChange={e => set('customer_code', e.target.value)}>
              <option value="">— seleziona —</option>
              {customers.map(c => (
                <option key={c.customer_code} value={c.customer_code}>{c.customer_name} ({c.customer_code})</option>
              ))}
            </select>
          </label>
          <label style={s.field}>
            <span style={s.label}>Data del test *</span>
            <input style={s.input} type="date" value={form.tested_at} max={todayISO()}
              onChange={e => set('tested_at', e.target.value)} required />
          </label>
          <label style={s.field}>
            <span style={s.label}>Infrastruttura / nodo</span>
            <input style={s.input} list={infraListId} value={form.infrastructure} placeholder="es. PVE1"
              maxLength={120} onChange={e => set('infrastructure', e.target.value)} />
            <datalist id={infraListId}>
              {infraSuggestions.map(i => <option key={i} value={i} />)}
            </datalist>
          </label>
          <label style={s.field}>
            <span style={s.label}>ID VM</span>
            <input style={s.input} value={form.vm_id} placeholder="es. 110" maxLength={50}
              onChange={e => set('vm_id', e.target.value)} />
          </label>
          <label style={{ ...s.field, gridColumn: '1 / -1' }}>
            <span style={s.label}>Nome VM *</span>
            <input style={s.input} value={form.vm_name} placeholder="es. SRV-SMTP-RM" maxLength={200}
              onChange={e => set('vm_name', e.target.value)} />
          </label>
          <label style={s.field}>
            <span style={s.label}>Esito *</span>
            <select style={s.input} value={form.outcome} onChange={e => set('outcome', e.target.value)}>
              {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label style={s.field}>
            <span style={s.label}>Cadenza del restore test</span>
            <select style={s.input} value={form.cadence} onChange={e => set('cadence', e.target.value)}>
              <option value="">— non indicata —</option>
              {CADENCES.map(c => <option key={c} value={c}>{cadenceLabel(c)}</option>)}
            </select>
          </label>
          <label style={{ ...s.field, justifyContent: 'flex-end' }}>
            <span style={s.checkRow}>
              <input type="checkbox" checked={form.restore_removed}
                onChange={e => set('restore_removed', e.target.checked)} />
              Restore di prova rimosso dopo il test
            </span>
          </label>
          <label style={{ ...s.field, gridColumn: '1 / -1' }}>
            <span style={s.label}>Note</span>
            <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={form.notes} maxLength={4000}
              placeholder="Cosa è stato verificato (avvio, login, applicazione, dati…)"
              onChange={e => set('notes', e.target.value)} />
          </label>
        </div>

        <div style={s.label}>Screenshot di evidenza</div>
        <div style={s.dropZone} onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
          <div style={{ marginBottom: 8, color: '#64748b', fontSize: 13 }}>
            Trascina qui le immagini, <b>incolla con Ctrl+V</b> uno screenshot, oppure
          </div>
          <button type="button" style={s.btnSecondary} onClick={() => fileInput.current?.click()}>
            📎 Scegli file
          </button>
          <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple
            style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          <div style={s.thumbRow}>
            {existing.map(ev => (
              <EvidenceThumb key={ev.id} ev={ev} onOpen={(u, n) => setLightbox({ u, n })} onDelete={deleteExisting} />
            ))}
            {pending.map((p, i) => (
              <PendingThumb key={p.url} item={p} onRemove={() => removePending(i)} />
            ))}
          </div>
        </div>

        {error && <div style={s.error}>{error}</div>}

        <div style={s.modalFooter}>
          <button type="button" style={s.btnSecondary} onClick={onClose} disabled={saving}>Annulla</button>
          <button type="submit" style={{ ...s.btnPrimary, background: theme.primaryColor }} disabled={saving}>
            {saving ? 'Salvataggio…' : editing ? 'Salva modifiche' : 'Registra restore'}
          </button>
        </div>
      </form>
      {lightbox && <Lightbox src={lightbox.u} name={lightbox.n} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function RestoreDetailModal({ id, canEdit, onClose, onEdit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    restoreAPI.get(id).then(r => setData(r.data)).catch(e => setError(errorMessage(e)));
  }, [id]);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Dettaglio restore test</h3>
          <button type="button" style={s.iconBtn} onClick={onClose} aria-label="Chiudi">✕</button>
        </div>
        {error && <div style={s.error}>{error}</div>}
        {!data && !error && <div style={s.empty}>Caricamento…</div>}
        {data && (
          <>
            <div style={s.detailGrid}>
              <div><span style={s.label}>Cliente</span><div>{data.customer_name} ({data.customer_code})</div></div>
              <div><span style={s.label}>Data</span><div>{formatDate(data.tested_at)}</div></div>
              <div><span style={s.label}>Infrastruttura</span><div>{data.infrastructure || '—'}</div></div>
              <div><span style={s.label}>VM</span><div>{[data.vm_id, data.vm_name].filter(Boolean).join(' · ')}</div></div>
              <div><span style={s.label}>Esito</span><div><OutcomeBadge outcome={data.outcome} /></div></div>
              <div><span style={s.label}>Restore rimosso</span><div>{data.restore_removed ? 'Sì' : 'No'}</div></div>
              <div><span style={s.label}>Operatore</span><div>{data.performed_by_name || data.performed_by || '—'}</div></div>
              <div><span style={s.label}>Cadenza</span><div>{cadenceLabel(data.cadence)}</div></div>
              <div><span style={s.label}>Registrato il</span><div>{new Date(data.created_at).toLocaleString('it-IT')}</div></div>
            </div>
            {data.notes && (
              <div style={{ marginTop: 12 }}>
                <span style={s.label}>Note</span>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#334155' }}>{data.notes}</div>
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              <span style={s.label}>Screenshot ({data.evidence.length})</span>
              {data.evidence.length === 0
                ? <div style={{ color: '#94a3b8', fontSize: 13 }}>Nessuno screenshot allegato</div>
                : <div style={s.thumbRow}>
                    {data.evidence.map(ev => (
                      <EvidenceThumb key={ev.id} ev={ev} onOpen={(u, n) => setLightbox({ u, n })} />
                    ))}
                  </div>}
            </div>
            <div style={s.modalFooter}>
              {canEdit && <button type="button" style={s.btnSecondary} onClick={() => onEdit(data)}>✏️ Modifica</button>}
              <button type="button" style={s.btnSecondary} onClick={onClose}>Chiudi</button>
            </div>
          </>
        )}
      </div>
      {lightbox && <Lightbox src={lightbox.u} name={lightbox.n} onClose={() => setLightbox(null)} />}
    </div>
  );
}

export default function RestoreRegisterPage({ customer }) {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [form, setForm] = useState(null);     // null | { test: obj|null }
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    backupAPI.getCustomers().then(r => setCustomers(r.data || [])).catch(() => {});
  }, []);

  // Ricerca testuale con debounce per non interrogare il server a ogni tasto.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filters = useMemo(() => {
    const p = {};
    if (customer) p.customer = customer;
    if (outcome) p.outcome = outcome;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (query) p.q = query;
    return p;
  }, [customer, outcome, dateFrom, dateTo, query]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    restoreAPI.list(filters)
      .then(r => setData(r.data))
      .catch(e => setError(errorMessage(e, 'Errore nel caricamento dei restore test')))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const infraSuggestions = useMemo(
    () => [...new Set((data?.items || []).map(i => i.infrastructure).filter(Boolean))].sort(),
    [data]
  );

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams(filters).toString();
      const res = await fetch(`/api/restore-tests/export.${format}${params ? `?${params}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export fallito');
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `restore-register.${format}`;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Errore durante l'export. Riprova.");
    } finally {
      setExporting(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Eliminare il restore test del ${formatDate(row.tested_at)} su "${row.vm_name}" (${row.customer_name})?\nVerranno eliminati anche i suoi screenshot.`)) return;
    try {
      await restoreAPI.remove(row.id);
      load();
    } catch (err) { alert(errorMessage(err)); }
  };

  const canEdit = !!data?.can_edit;
  const stats = data?.stats;
  const items = data?.items || [];
  const rateColor = stats?.success_rate == null ? '#64748b'
    : stats.success_rate === 100 ? '#16a34a' : stats.success_rate >= 80 ? '#ca8a04' : '#dc2626';

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.pageTitle}>Registro Restore</h2>
        <div style={s.actions}>
          <button style={s.btnExport} disabled={!!exporting} onClick={() => handleExport('xlsx')}>
            {exporting === 'xlsx' ? '⏳ Generazione...' : '📊 Esporta Excel'}
          </button>
          <button style={s.btnExport} disabled={!!exporting} onClick={() => handleExport('pdf')}>
            {exporting === 'pdf' ? '⏳ Generazione...' : '📄 Esporta PDF'}
          </button>
          {canEdit && (
            <button style={{ ...s.btnPrimary, background: theme.primaryColor }} onClick={() => setForm({ test: null })}>
              ＋ Nuovo restore test
            </button>
          )}
        </div>
      </div>

      <div style={s.kpiRow}>
        <KPICard label="Restore eseguiti" value={stats?.total ?? 0} color="#2563eb"
          sub={stats?.ultimo_test ? `ultimo: ${formatDate(stats.ultimo_test)}` : 'nessuno registrato'} />
        <KPICard label="Esito positivo" value={stats?.success_rate == null ? '—' : `${stats.success_rate}%`}
          color={rateColor} sub={`${stats?.positivi ?? 0} su ${stats?.total ?? 0}`} />
        <KPICard label="Negativi" value={stats?.negativi ?? 0} color="#dc2626" />
        <KPICard label="Parziali" value={stats?.parziali ?? 0} color="#ca8a04" />
        <KPICard label="Senza screenshot" value={stats?.senza_evidenze ?? 0}
          color={stats?.senza_evidenze ? '#ca8a04' : '#16a34a'} sub="evidenza mancante" />
      </div>

      <div style={s.filters}>
        <select style={s.filterInput} value={outcome} onChange={e => setOutcome(e.target.value)}>
          <option value="">Tutti gli esiti</option>
          {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <label style={s.filterLabel}>Dal
          <input style={s.filterInput} type="date" value={dateFrom} max={dateTo || undefined}
            onChange={e => setDateFrom(e.target.value)} />
        </label>
        <label style={s.filterLabel}>Al
          <input style={s.filterInput} type="date" value={dateTo} min={dateFrom || undefined}
            onChange={e => setDateTo(e.target.value)} />
        </label>
        <input style={{ ...s.filterInput, minWidth: 220 }} type="search" placeholder="Cerca VM, nodo, note…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {(outcome || dateFrom || dateTo || search) && (
          <button style={s.btnLink} onClick={() => { setOutcome(''); setDateFrom(''); setDateTo(''); setSearch(''); }}>
            Azzera filtri
          </button>
        )}
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.card}>
        {loading && !data ? <div style={s.empty}>Caricamento...</div>
          : items.length === 0 ? (
            <div style={s.empty}>
              Nessun restore test registrato{customer || outcome || dateFrom || dateTo || query ? ' per i filtri selezionati' : ''}.
              {canEdit && !(customer || outcome || dateFrom || dateTo || query) && ' Usa "Nuovo restore test" per registrare il primo.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Data', 'Cliente', 'Infrastruttura', 'VM', 'Esito', 'Cadenza', 'Operatore', 'Restore rimosso', 'Note', 'Screenshot', ''].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r, i) => (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={s.td}>{formatDate(r.tested_at)}</td>
                      <td style={s.td}>{r.customer_name}</td>
                      <td style={s.td}>{r.infrastructure || '—'}</td>
                      <td style={s.td}>{[r.vm_id, r.vm_name].filter(Boolean).join(' · ')}</td>
                      <td style={s.td}><OutcomeBadge outcome={r.outcome} /></td>
                      <td style={s.td}>{cadenceLabel(r.cadence)}</td>
                      <td style={s.td}>{r.performed_by_name || r.performed_by || '—'}</td>
                      <td style={s.td}>{r.restore_removed ? 'Sì' : <span style={{ color: '#ca8a04', fontWeight: 600 }}>No</span>}</td>
                      <td style={{ ...s.td, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.notes || ''}>
                        {r.notes || '—'}
                      </td>
                      <td style={s.td}>
                        {r.evidence_count > 0
                          ? <button style={s.btnLink} onClick={() => setDetailId(r.id)}>🖼 {r.evidence_count}</button>
                          : <span style={{ color: '#ca8a04' }}>nessuno</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        <button style={s.btnLink} onClick={() => setDetailId(r.id)}>Dettagli</button>
                        {canEdit && (
                          <>
                            <button style={s.btnLink} onClick={() => setForm({ test: r })}>Modifica</button>
                            <button style={{ ...s.btnLink, color: '#dc2626' }} onClick={() => handleDelete(r)}>Elimina</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {form && (
        <RestoreFormModal
          test={form.test}
          customers={customers}
          infraSuggestions={infraSuggestions}
          defaultCustomer={customer}
          onClose={() => setForm(null)}
          onSaved={load}
        />
      )}
      {detailId && (
        <RestoreDetailModal
          id={detailId}
          canEdit={canEdit}
          onClose={() => setDetailId(null)}
          onEdit={(row) => { setDetailId(null); setForm({ test: row }); }}
        />
      )}
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  pageTitle: { margin: 0, fontSize: 22, color: '#1e293b' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  btnExport: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#fff', color: '#1e293b', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnPrimary: { padding: '8px 16px', borderRadius: 8, border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#fff', color: '#1e293b', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnLink: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '2px 6px' },
  kpiRow: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 },
  kpi: { flex: '1 1 160px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px' },
  kpiLabel: { fontSize: 12, color: '#64748b', fontWeight: 600 },
  kpiValue: { fontSize: 28, fontWeight: 800, lineHeight: 1.2 },
  kpiSub: { fontSize: 11, color: '#94a3b8' },
  filters: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 },
  filterLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' },
  filterInput: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, color: '#1e293b', background: '#fff' },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 20, overflow: 'hidden' },
  empty: { padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 16px', background: '#f8fafc', color: '#64748b', fontWeight: 600, textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  td: { padding: '10px 16px', color: '#334155', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' },
  error: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, margin: '10px 0' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 720, boxShadow: '0 20px 50px rgba(0,0,0,0.25)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 },
  iconBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' },
  input: { padding: '8px 10px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 14, color: '#1e293b', background: '#fff', fontFamily: 'inherit' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1e293b', padding: '8px 0' },
  dropZone: { border: '2px dashed #cbd5e1', borderRadius: 10, padding: 14, textAlign: 'center', marginTop: 6, background: '#f8fafc' },
  thumbRow: { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12, justifyContent: 'flex-start' },
  thumbBox: { position: 'relative', width: 120, textAlign: 'left' },
  thumbImg: { width: 120, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'zoom-in', display: 'block', background: '#fff' },
  thumbPlaceholder: { width: 120, height: 84, borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12 },
  thumbName: { fontSize: 11, color: '#64748b', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  thumbDelete: { position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: 11, border: 'none', background: 'rgba(220,38,38,0.92)', color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: '22px', padding: 0 },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, fontSize: 14, color: '#1e293b' },
  lightbox: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' },
  lightboxImg: { maxWidth: '96vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 6, cursor: 'default' },
  lightboxCaption: { color: '#cbd5e1', fontSize: 12, marginTop: 10 },
};
